import localhost from "apis/localhost";
import { extractLockedByUser } from "apis/utils";
import { ARCHIVE_STATUS_BASE_URL } from "constants/archiveStatuses";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  disableNavBarAndSideBar,
  removeFromJobQueue,
} from "slices/app/appSlice";
import { reduceByMax } from "utils/collections";

import { buildArchiveStatusSelectors } from "./archiveStatusesSelectors";
import {
  addStatus,
  removeStatus,
  setEditableStatuses,
  setIsDirty,
  setIsEditing,
  setIsLoading,
  setIsLocked,
  setLockedBy,
  setStatuses,
  updateStatus,
} from "./archiveStatusesSlice";

const LOCKED_STATUS = 423;

const toPayload = (statuses) =>
  statuses.map(({ isNew, id, name }) => (isNew ? { name } : { id, name }));

export const buildArchiveStatusActions = (entityType) => {
  const baseUrl = `${ARCHIVE_STATUS_BASE_URL}/${entityType}`;
  const { selectItems, selectEditableItems } =
    buildArchiveStatusSelectors(entityType);

  const lockEditing = () => async (dispatch) => {
    dispatch(addToJobQueue("lockArchiveStatusesEditing"));
    try {
      await localhost.post(`${baseUrl}/lock`);
      return true;
    } catch (error) {
      if (error.status === LOCKED_STATUS) {
        dispatch(setIsLocked({ entityType, isLocked: true }));
        dispatch(setLockedBy({ entityType, lockedBy: extractLockedByUser(error) }));
      } else {
        dispatch(showErrorModal(error));
      }
      return false;
    } finally {
      dispatch(removeFromJobQueue("lockArchiveStatusesEditing"));
    }
  };

  const unlockEditing = () => async (dispatch) => {
    dispatch(addToJobQueue("unlockArchiveStatusesEditing"));
    try {
      await localhost.post(`${baseUrl}/unlock`);
    } catch (error) {
      dispatch(showErrorModal(error));
    } finally {
      dispatch(removeFromJobQueue("unlockArchiveStatusesEditing"));
    }
  };

  const fetchItems = () => async (dispatch) => {
    dispatch(setIsLoading({ entityType, isLoading: true }));
    try {
      const rsp = await localhost.get(baseUrl);
      const { statuses, locked, updatedBy } = rsp.data;

      dispatch(setStatuses({ entityType, statuses }));
      dispatch(setIsLocked({ entityType, isLocked: Boolean(locked) }));
      dispatch(setLockedBy({ entityType, lockedBy: locked ? updatedBy : "" }));
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(setIsLoading({ entityType, isLoading: false }));
    }
  };

  const openEditMode = () => async (dispatch, getState) => {
    const isLocked = await dispatch(lockEditing());
    if (isLocked) {
      dispatch(setIsEditing({ entityType, isEditing: true }));
      dispatch(setEditableStatuses({ entityType, statuses: selectItems(getState()) }));
      dispatch(disableNavBarAndSideBar(true));
    }
  };

  const exitEditMode = () => async (dispatch) => {
    await dispatch(unlockEditing());
    dispatch(setIsEditing({ entityType, isEditing: false }));
    dispatch(setEditableStatuses({ entityType, statuses: [] }));
    dispatch(setIsDirty({ entityType, isDirty: false }));
    dispatch(disableNavBarAndSideBar(false));
  };

  const saveChanges = () => async (dispatch, getState) => {
    dispatch(addToJobQueue("saveArchiveStatusChanges"));
    try {
      const response = await localhost.patch(
        `${baseUrl}/edit`,
        toPayload(selectEditableItems(getState()))
      );
      dispatch(setStatuses({ entityType, statuses: response.data.statuses || [] }));
      dispatch(exitEditMode());
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(removeFromJobQueue("saveArchiveStatusChanges"));
    }
  };

  const addNewItem = (index) => (dispatch, getState) => {
    const statuses = selectEditableItems(getState());
    const maxId = statuses.reduce(reduceByMax("id"), 0);
    const newStatus = {
      id: Number.parseInt(maxId || 0, 10) + 100,
      isNew: true,
      name: "",
      isSystem: false,
      archivedCount: 0,
    };

    dispatch(addStatus({ entityType, index, status: newStatus }));
    return newStatus;
  };

  const removeFromEditableStatuses = (dispatch, getState, statusId) => {
    const currentEditableStatuses = selectEditableItems(getState());
    const editableIndex = currentEditableStatuses.findIndex(
      (item) => item.id === statusId
    );
    if (editableIndex !== -1) {
      dispatch(removeStatus({ entityType, index: editableIndex }));
    }
  };

  const applyReplacementCount = (dispatch, getState, replacementId, serverStatuses) => {
    const replacementFromServer = serverStatuses.find(
      (item) => item.id === replacementId
    );
    if (!replacementFromServer) {
      return;
    }

    const currentEditableStatuses = selectEditableItems(getState());
    const replacementIndex = currentEditableStatuses.findIndex(
      (item) => item.id === replacementId
    );
    if (replacementIndex !== -1) {
      dispatch(
        updateStatus({
          entityType,
          index: replacementIndex,
          changes: { archivedCount: replacementFromServer.archivedCount },
        })
      );
    }
  };

  const removeItem = (index) => async (dispatch, getState) => {
    const status = selectEditableItems(getState())[index];
    if (status?.isNew) {
      dispatch(removeStatus({ entityType, index }));
      return;
    }

    dispatch(addToJobQueue("deleteArchiveStatus"));
    try {
      const response = await localhost.delete(`${baseUrl}/${status.id}`);
      dispatch(setStatuses({ entityType, statuses: response.data.statuses || [] }));
      removeFromEditableStatuses(dispatch, getState, status.id);
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(removeFromJobQueue("deleteArchiveStatus"));
    }
  };

  const reassignAndDelete = (index, replacementId) => async (dispatch, getState) => {
    const status = selectEditableItems(getState())[index];

    dispatch(addToJobQueue("deleteArchiveStatus"));
    try {
      const response = await localhost.delete(
        `${baseUrl}/${status.id}?replacementId=${replacementId}`
      );
      const serverStatuses = response.data.statuses || [];
      dispatch(setStatuses({ entityType, statuses: serverStatuses }));
      removeFromEditableStatuses(dispatch, getState, status.id);
      applyReplacementCount(dispatch, getState, replacementId, serverStatuses);
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(removeFromJobQueue("deleteArchiveStatus"));
    }
  };

  return {
    fetchItems,
    openEditMode,
    exitEditMode,
    saveChanges,
    addNewItem,
    setEditableItems: (statuses) => setEditableStatuses({ entityType, statuses }),
    setIsDirty: (isDirty) => setIsDirty({ entityType, isDirty }),
    removeItem,
    reassignAndDelete,
  };
};
